import React from 'react';
import { Box, Typography, alpha } from '@mui/material';

/**
 * Kanban-aligned status selector for forms.
 * @param {object[]} columns - same shape as Kanban columns (id, label, shortLabel, color, bg, border)
 */
const StatusColumnPicker = ({
  columns,
  value,
  onChange,
  disabled = false,
  label = 'Status',
  helperText,
  error = false,
}) => {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.875rem',
          color: alpha('#38BDF8', 0.55),
          mb: 1,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${Math.min(columns.length, 4)}, 1fr)` },
          gap: 1.25,
        }}
      >
        {columns.map((col) => {
          const selected = value === col.id;
          return (
            <Box
              key={col.id}
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onChange(col.id)}
              sx={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.55 : 1,
                textAlign: 'left',
                p: 1.25,
                borderRadius: '10px',
                border: `1px solid ${selected ? col.border : 'rgba(148,163,184,0.2)'}`,
                background: selected
                  ? `linear-gradient(160deg, ${col.bg} 0%, rgba(15,23,42,0.85) 100%)`
                  : 'rgba(15,23,42,0.5)',
                boxShadow: selected ? `0 0 12px ${alpha(col.color, 0.25)}` : 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
                '&:hover': disabled
                  ? {}
                  : {
                      borderColor: col.border,
                      background: `linear-gradient(160deg, ${col.bg} 0%, rgba(15,23,42,0.7) 100%)`,
                    },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: col.color,
                    boxShadow: selected ? `0 0 6px ${col.color}` : 'none',
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    color: selected ? '#fff' : 'rgba(255,255,255,0.75)',
                    letterSpacing: 0.3,
                  }}
                >
                  {col.shortLabel || col.label}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  color: 'rgba(255,255,255,0.45)',
                  fontFamily: "'DM Sans', sans-serif",
                  pl: 1.6,
                }}
              >
                {col.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {helperText && (
        <Typography
          sx={{
            mt: 0.75,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.68rem',
            letterSpacing: 0.3,
            color: error ? '#F87171' : 'rgba(226,232,240,0.75)',
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default StatusColumnPicker;
